/**
 * ContractorDashboard — what needs attention in contractor management today.
 *
 * Built on the Common Dashboard Framework (@/dashboard), so every KPI is
 * RBAC-checked and site-scoped server-side, drills to the records behind it, and
 * exports to PDF / Excel / CSV / PowerPoint without this page implementing any
 * of that. The widget catalog is @/dashboard/presets/contractor.
 *
 * Deliberately opens with "needs attention" rather than a population count. An
 * EHS lead does not open this to learn how many contractors exist; they open it
 * to find the eight documents expiring and the five workers waiting on
 * induction. Every one of those numbers is clickable.
 */
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Info, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import {
  CustomizableDashboard, FilterBar, ExportMenu, useTimeFilter,
} from '@/dashboard';
import {
  buildContractorCatalog, contractorDefaultLayout, contractorExportSpec,
} from '@/dashboard/presets/contractor';

const DASHBOARD_KEY = 'contractor';
const CATALOG = buildContractorCatalog();
const DEFAULT_LAYOUT = contractorDefaultLayout();

/**
 * Said plainly, because a reader would otherwise assume the site filter narrows
 * everything on the page.
 */
const DATA_NOTES = [
  'Firm-level figures (contractors, pending approval, suspended, documents expiring) are organisation-wide: contractor_companies carries no site, so they do not narrow with the site filter. Worker figures do narrow, via the worker’s site and the contractor’s site assignment.',
  'Medical fitness figures read a validity DATE only. No clinical detail is read, stored or shown here — occupational health remains a separate, access-controlled module.',
  'Contractor EHS performance counts records explicitly attributed to a contractor. A record where nobody set the contractor is not counted against anyone, so these figures understate rather than guess.',
  'Eligibility enforcement at permit issue is controlled per deployment (PTW_CONTRACTOR_ELIGIBILITY). Where it is advisory, a blocked worker is reported but does not stop a permit.',
];

export default function ContractorDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { activeSiteId } = useSite();
  const { preset, dateFrom, dateTo, time, setFilter } =
    useTimeFilter({ preset: 'last_30_days' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [showNotes, setShowNotes] = useState(false);

  const buildSpec = useCallback(
    (visibleIds) => contractorExportSpec(visibleIds), [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Contractor Dashboard
            </h1>
            <p className="mt-1 max-w-2xl text-gray-600">
              Contractor compliance, worker eligibility and contractor EHS
              performance. Every figure opens the records behind it.
            </p>
            <button
              type="button"
              onClick={() => setShowNotes((v) => !v)}
              aria-expanded={showNotes}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium
                         text-gray-500 hover:text-gray-700"
            >
              <Info className="h-3.5 w-3.5" />
              What this dashboard does not cover
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setRefreshKey((k) => k + 1)}
                    title="Reload every panel">
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate('/contractors')}>
              <Users className="mr-2 h-4 w-4" /> Contractors
            </Button>
            <Button variant="outline" onClick={() => navigate('/safety')}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </div>
        </div>

        {showNotes && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-gray-800">
              Known data limitations
            </h2>
            <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-gray-600">
              {DATA_NOTES.map((note) => <li key={note}>{note}</li>)}
            </ul>
          </div>
        )}

        <CustomizableDashboard
          key={refreshKey}
          dashboardKey={DASHBOARD_KEY}
          module="contractor"
          siteId={activeSiteId}
          time={time}
          compare
          catalog={CATALOG}
          defaultLayout={DEFAULT_LAYOUT}
          filterBar={(
            <FilterBar
              preset={preset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={setFilter}
              extra={(
                <ExportMenu module="contractor" specBuilder={buildSpec} />
              )}
            />
          )}
        />
      </div>
    </div>
  );
}
